import React, { useState } from 'react';
import './Tabs.scss';
import { Svg } from 'src/components/Svg/Svg';
import { SvgIconNames } from 'src/components/Svg/icons-types';

import { useBreakpointMatch } from '../../hooks/useBreakpoint';

export type TabItem = {
    title: string;
    id: string;
    icon?: string;
    content: React.ReactNode;
    strategy?: 'css' | 'mount';
};

export type TabsProps = {
    tabs: TabItem[];
    strategy?: 'css' | 'mount';
    collapseAll?: boolean;
};

export const Tabs: React.FC<TabsProps> = ({ tabs, strategy = 'css', collapseAll }) => {
    const { isDesk, isMob } = useBreakpointMatch();
    const [openIndexes, toggleTab] = useState(collapseAll ? [] : ([0] as number[]));
    const openTabs = (() => {
        if (isDesk && openIndexes.length !== 1) {
            return [0];
        }
        return openIndexes;
    })();

    return (
        <div className="tabs">
            {tabs.map((item, index) => {
                const active = openTabs.indexOf(index) > -1;
                const buttonId = `${item.id}-tab`;
                const panelId = `${item.id}-panel`;
                return (
                    <React.Fragment key={item.id}>
                        <span className={`tabs__item ${active && 'active'}`}>
                            <button
                                type="button"
                                id={buttonId}
                                className={`tabs__link ${active && 'active'}`}
                                aria-expanded={active}
                                aria-controls={panelId}
                                onClick={() => {
                                    toggleTab((openTabIndexes) => {
                                        if (isMob) {
                                            if (openTabIndexes.indexOf(index) > -1) {
                                                return openTabIndexes.filter((x) => x !== index);
                                            }
                                            return openTabIndexes.concat(index);
                                        }
                                        return [index];
                                    });
                                }}
                            >
                                <span className="tabs__title">
                                    {item.icon && <Svg name={item.icon as SvgIconNames} />} {item.title}
                                </span>
                                <Svg name="arrow-down" />
                            </button>
                        </span>
                        {strategy === 'css' && item.strategy !== 'mount' && (
                            <div
                                id={panelId}
                                role="region"
                                aria-labelledby={buttonId}
                                className={`tabs__body ${active && 'active'}`}
                            >
                                {item.content}
                            </div>
                        )}
                        {(strategy === 'mount' || item.strategy === 'mount') && active && (
                            <div id={panelId} role="region" aria-labelledby={buttonId} className="tabs__body active">
                                {item.content}
                            </div>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};
